import type { Dashboard } from '@clockwise/clients/dashboards';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, LayoutDashboard, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { RequirePermission } from '@/components/custom/require-permission';
import { ShareBadge } from '@/components/custom/share-badge';
import { dashboardListQuery } from '@/components/dashboard/custom/dashboard-list-query';
import { emptyDashboard } from '@/components/dashboard/custom/new-dashboard';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardsApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/dashboards/')({
  component: DashboardsIndexPage,
});

function DashboardsIndexPage() {
  const { permissions } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ONE request for both sections — see `dashboardListQuery`.
  const saved = useQuery(dashboardListQuery());

  const create = useMutation({
    // NAMED AROUND WHAT IS ALREADY THERE. Every new dashboard used to be
    // "Untitled dashboard", which the per-owner unique name refuses the second
    // time — and with no rename anywhere, that left the only way into the
    // feature permanently answering 409. The list is already loaded here, so
    // the free name costs nothing to find; the server still has the last word
    // if two tabs race, and reports it as a conflict rather than a 500.
    mutationFn: () => dashboardsApi.create(emptyDashboard(freeName(mine))),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all });
      // Straight into edit mode: a dashboard with no tiles has nothing to read,
      // so landing on its read view would be a blank page and a second click.
      navigate({
        to: '/dashboards/$dashboardId',
        params: { dashboardId: data.id },
        search: { edit: true },
      });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not create the dashboard.'),
  });

  const mine = saved.data?.data.filter((d) => d.owned) ?? [];
  const shared = saved.data?.data.filter((d) => !d.owned) ?? [];
  // 200 is the server's ceiling, so a tenant past it loses the remainder.
  // Silent truncation is the failure mode to avoid: someone whose dashboard is
  // missing has no way to tell "cut off" from "deleted".
  const total = saved.data?.meta.total ?? 0;
  const truncated = total > (saved.data?.data.length ?? 0);

  return (
    <PageShell>
      <PageHeader title="Dashboards" subtitle="Put your saved reports on one page.">
        <RequirePermission permission="dashboard.edit">
          {/* NOT UNTIL THE LIST IS IN. `freeName` picks the next unused name
              from what is already there, and before the query resolves that is
              an empty array — so an eager click would ask for the default name
              again and be refused as a conflict. */}
          <Button onClick={() => create.mutate()} disabled={create.isPending || !saved.isSuccess}>
            <Plus className="size-4" aria-hidden="true" />
            New dashboard
          </Button>
        </RequirePermission>
      </PageHeader>

      {/* G15: a list that failed to load must not read as a list with nothing
          in it — "you have no dashboards" is a claim, and this is the one case
          where it would be a false one. */}
      {saved.isError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="min-w-0">
            <span className="font-medium text-destructive">Couldn’t load your dashboards.</span>{' '}
            {saved.error instanceof ApiError ? saved.error.message : 'Please try again.'}
          </p>
        </div>
      )}

      {saved.isPending && (
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {/* A heading over nothing is noise, and a permanent empty section teaches
          people to skip the heading — which is the heading that has to catch
          the eye on the day something appears under it. "My dashboards"
          survives one empty case: somebody who CAN build one, for whom the
          blank state is an invitation rather than a dead end. */}
      {saved.isSuccess && (
        <>
          {(mine.length > 0 || permissions.has('dashboard.edit')) && (
            <Section
              title="My dashboards"
              empty="Nothing yet — start one and drop a saved report onto it."
              dashboards={mine}
            />
          )}
          {shared.length > 0 && <Section title="Shared with me" dashboards={shared} />}
          {truncated && (
            <p className="text-muted-foreground text-sm">
              Showing {saved.data.data.length} of {total} dashboards.
            </p>
          )}
        </>
      )}
    </PageShell>
  );
}

/**
 * `"Untitled dashboard"`, then `"Untitled dashboard 2"`, and so on.
 *
 * Case-insensitively, because the unique index is `lower(name)`. Bounded
 * rather than `while (true)`: past a couple of dozen the person wants to name
 * the thing, and an unbounded search against a unique index is a click that
 * can hang. The last candidate is allowed to collide — the server answers 409
 * and the toast says so, which is the honest end of this road.
 */
function freeName(existing: readonly Dashboard[]): string {
  const taken = new Set(existing.map((d) => d.name.trim().toLowerCase()));
  for (let n = 1; n <= 50; n++) {
    const candidate = n === 1 ? BASE_NAME : `${BASE_NAME} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${BASE_NAME} ${existing.length + 1}`;
}

const BASE_NAME = 'Untitled dashboard';

function Section({
  title,
  dashboards,
  empty,
}: {
  title: string;
  dashboards: Dashboard[];
  empty?: string;
}) {
  return (
    <section className="space-y-2">
      <h2 className="font-medium text-sm">{title}</h2>
      {dashboards.length === 0 ? (
        <p className="text-muted-foreground text-sm">{empty}</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {dashboards.map((d) => (
            <li key={d.id}>
              <Link
                to="/dashboards/$dashboardId"
                params={{ dashboardId: d.id }}
                // `surface-card` — a bordered box with no background was
                // invisible against the cream page instead of reading as a
                // card (same fix as the reports listing tiles).
                className="surface-card flex h-full flex-col gap-2 rounded-lg border p-4 transition-colors hover:border-primary hover:bg-accent/40"
              >
                <span className="flex items-center gap-2 font-medium">
                  <LayoutDashboard
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="truncate">{d.name}</span>
                  <ShareBadge owned={d.owned} shares={d.shares} />
                </span>
                <span className="text-sm text-muted-foreground">
                  {d.description ?? `${d.definition.tiles.length} tiles`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
